import * as React from 'react';
import CommonDataTable from '../../components/CommonDataTable';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { usePermission } from "../../hooks/usePermission";
import { toast } from "../../components/AppToast";
import { fetchPO } from '../../services/purchaseOrder/purchaseOrder';

// Action Menu Component
function ActionMenu({ params, onView, onEdit }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const { hasPermission } = usePermission();
  const canViewPO = hasPermission("poDetails", "VIEW");
  const canUpdatePO = hasPermission("poDetails", "UPDATE");

  const row = params?.row || {};
  const po = row?.purchase_order || {};

  // -------------------------------
  // Check SOURCE (must be INTERNAL)
  // -------------------------------
  const sourceValue = String(po?.source || row?.source || "")
    .trim()
    .toLowerCase();

  const isInternalSource = sourceValue === "internal";

  // -------------------------------
  // Check STATUS (must be ONLY New)
  // -------------------------------
  const statusValue = String(po?.status || row?.status || "")
    .trim()
    .toLowerCase();

  const isStatusNew = statusValue === "new";

  // -------------------------------
  // FINAL EDIT PERMISSION RULE
  // -------------------------------
  const finalCanEdit = canUpdatePO && isInternalSource && isStatusNew;

  // -------------------------------
  // Handlers
  // -------------------------------
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleView = () => {
    if (!canViewPO) {
      toast.error("You do not have permission to view Purchase Orders!", {
        position: "top-center",
      });
      return;
    }
    onView?.(params.row);
    handleClose();
  };

  const handleEditClick = () => {
    if (!canUpdatePO) {
      toast.error("You do not have permission to edit Purchase Orders!", {
        position: "top-center",
      });
      return;
    }

    if (!isInternalSource) {
      toast.error("Only INTERNAL source Purchase Orders can be edited.", {
        position: "top-center",
      });
      return;
    }

    if (!isStatusNew) {
      toast.error("Only NEW status Purchase Orders can be edited.", {
        position: "top-center",
      });
      return;
    }

    onEdit?.({ ...params.row, __isEditMode: true });
    handleClose();
  };

  // -------------------------------
  // UI
  // -------------------------------
  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        aria-controls={open ? "action-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
      >
        {/* VIEW */}
        <MenuItem onClick={handleView}>
          <i className="bi bi-eye me-2"></i>
          View
        </MenuItem>

        {/* EDIT RULE BASED */}
        {finalCanEdit ? (
          <MenuItem onClick={handleEditClick}>
            <i className="bi bi-pencil me-2"></i>
            Edit
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              if (!canUpdatePO) {
                toast.error("You do not have permission to edit Purchase Orders!");
              } else if (!isInternalSource) {
                toast.error("Only INTERNAL source Purchase Orders can be edited.");
              } else if (!isStatusNew) {
                toast.error("Only NEW status Purchase Orders can be edited.");
              }
            }}
            style={{ opacity: 0.5, cursor: "not-allowed" }}
          >
            <i className="bi bi-pencil me-2"></i>
            Edit
          </MenuItem>
        )}
      </Menu>
    </>
  );
}


// Action menu column definition
const actionMenuColumn = (onView, onEdit) => ({
  field: 'actions',
  headerName: '',
  sortable: false,
  width: 60,
  renderCell: (params) => <ActionMenu params={params} onView={onView} onEdit={onEdit} />,
});

// Full list of possible columns
const allPossibleColumnsOLD = [
  { field: 'actions', headerName: '' },
  { field: 'purchaseOrder', headerName: 'Purchase Order', width: 150 },
  { field: 'buyerOwner', headerName: 'Buyer/Owner', width: 130 },
  { field: 'status', headerName: 'Status', width: 120 },
  { field: 'type', headerName: 'Type', width: 120 },
  { field: 'expectedReceiptDate', headerName: 'Expected Receipt Date', width: 180 },
  { field: 'supplier', headerName: 'Supplier', width: 150 },
  { field: 'totalOrderedQuantity', headerName: 'Total Order Qty', width: 130, type: 'number' },
  { field: 'openQuantity', headerName: 'Open Quantity', width: 130, type: 'number' },
  { field: 'buyersReference', headerName: "Buyer's Reference", width: 150 },
  { field: 'otherReference', headerName: 'Other Reference', width: 150 },
  { field: 'carrier', headerName: 'Carrier', width: 150 },
  { field: 'purchasingOfficeLocation', headerName: 'Purchasing Office Location', width: 180 },
  { field: 'buyerName', headerName: 'Buyer Name', width: 130 },
  { field: 'purchaseOrderDate', headerName: 'Created Date', width: 200 },
  { field: 'asnNumbers', headerName: 'ASN(s)', width: 240 },
  { field: 'externalPoNumber', headerName: 'External PO Number', width: 150 },
  { field: 'supplierReference', headerName: 'Supplier Reference', width: 150 },
  { field: 'totalWeight', headerName: 'Total Weight', width: 130, type: 'number' },
  { field: 'totalVolume', headerName: 'Total Volume', width: 130, type: 'number' },
  { field: 'closedDate', headerName: 'Closed Date', width: 150 },
  { field: 'source', headerName: 'Source', width: 120 },
  { field: 'totalPOPrice', headerName: 'Total PO Price', width: 180 },
  { field: 'buyerAddressLine1', headerName: 'Buyer Address Line 1', width: 180 },
  { field: 'buyerCity', headerName: 'Buyer City', width: 150 },
  { field: 'buyerState', headerName: 'Buyer State', width: 130 },
  { field: 'buyerCountry', headerName: 'Buyer Country', width: 130 },
  { field: 'addressLine1', headerName: 'Supplier Address Line 1', width: 180 },
  { field: 'city', headerName: 'Supplier City', width: 150 },
  { field: 'state', headerName: 'Supplier State', width: 130 },
  { field: 'country', headerName: 'Supplier Country', width: 130 },
  { field: 'poHeaderUdf1', headerName: 'PO Header UDF1', width: 150 },
  { field: 'poHeaderUdf2', headerName: 'PO Header UDF2', width: 150 },
  { field: 'poHeaderUdf3', headerName: 'PO Header UDF3', width: 150 },
  { field: 'poHeaderUdf4', headerName: 'PO Header UDF4', width: 150 },
  { field: 'poHeaderUdf5', headerName: 'PO Header UDF5', width: 150 },
  { field: 'apportionRule', headerName: 'Apportion Rule', width: 150 },
  { field: 'notes', headerName: 'Notes', width: 200 },
  { field: 'termsOfDelivery', headerName: 'Terms of Delivery', width: 150 },
  { field: 'termsOfPayment', headerName: 'Terms of Payment', width: 150 },
  { field: 'createByUser', headerName: 'Created By', width: 150 },
  { field: 'editedByUser', headerName: 'Updated By', width: 150 },
  { field: 'editDate', headerName: 'Updated Date', width: 180 },
  { field: 'loadingDate', headerName: 'Loading Date', width: 150 },
  { field: 'placeOfLoading', headerName: 'Place of Loading', width: 180 },
  { field: 'countryOfOrigin', headerName: 'Country of Origin', width: 150 },
  { field: 'placeOfDelivery', headerName: 'Place of Delivery', width: 180 },
  { field: 'countryOfDestination', headerName: 'Country of Destination', width: 180 },
  { field: 'placeOfDischarge', headerName: 'Place of Discharge', width: 180 },
  { field: 'customField1', headerName: 'PO Text UDF 1', width: 150 },
  { field: 'customField2', headerName: 'PO Text UDF 2', width: 150 },
  { field: 'customField4', headerName: 'PO Text UDF 3', width: 150 },
  { field: 'customField5', headerName: 'PO Text UDF 4', width: 150 },
];


const allPossibleColumns = [
  { field: 'actions', headerName: '' },               // keep small width only here if you want

  { field: 'purchaseOrder', headerName: 'Purchase Order' },
  { field: 'buyerOwner', headerName: 'Buyer/Owner' },
  { field: 'status', headerName: 'Status' },
  { field: 'type', headerName: 'Type' },
  { field: 'expectedReceiptDate', headerName: 'Expected Receipt Date' },
  { field: 'supplier', headerName: 'Supplier' },
  { field: 'totalOrderedQuantity', headerName: 'Total Order Qty', type: 'number' },
  { field: 'openQuantity', headerName: 'Open Quantity', type: 'number' },
  { field: 'buyersReference', headerName: "Buyer's Reference" },
  { field: 'otherReference', headerName: 'Other Reference' },
  { field: 'carrier', headerName: 'Carrier' },
  { field: 'purchasingOfficeLocation', headerName: 'Purchasing Office Location' },
  { field: 'buyerName', headerName: 'Buyer Name' },
  { field: 'purchaseOrderDate', headerName: 'Created Date' },
  { field: 'asnNumbers', headerName: 'ASN(s)' },
  { field: 'externalPoNumber', headerName: 'External PO Number' },
  { field: 'supplierReference', headerName: 'Supplier Reference' },
  { field: 'totalWeight', headerName: 'Total Weight', type: 'number' },
  { field: 'totalVolume', headerName: 'Total Volume', type: 'number' },
  { field: 'closedDate', headerName: 'Closed Date' },
  { field: 'source', headerName: 'Source' },
  { field: 'totalPOPrice', headerName: 'Total PO Price' },
  { field: 'buyerAddressLine1', headerName: 'Buyer Address Line 1' },
  { field: 'buyerCity', headerName: 'Buyer City' },
  { field: 'buyerState', headerName: 'Buyer State' },
  { field: 'buyerCountry', headerName: 'Buyer Country' },
  { field: 'addressLine1', headerName: 'Supplier Address Line 1' },
  { field: 'city', headerName: 'Supplier City' },
  { field: 'state', headerName: 'Supplier State' },
  { field: 'country', headerName: 'Supplier Country' },
  { field: 'poHeaderUdf1', headerName: 'PO Header UDF1' },
  { field: 'poHeaderUdf2', headerName: 'PO Header UDF2' },
  { field: 'poHeaderUdf3', headerName: 'PO Header UDF3' },
  { field: 'poHeaderUdf4', headerName: 'PO Header UDF4' },
  { field: 'poHeaderUdf5', headerName: 'PO Header UDF5' },
  { field: 'apportionRule', headerName: 'Apportion Rule' },
  { field: 'notes', headerName: 'Notes' },
  { field: 'termsOfDelivery', headerName: 'Terms of Delivery' },
  { field: 'termsOfPayment', headerName: 'Terms of Payment' },
  { field: 'createByUser', headerName: 'Created By' },
  { field: 'editedByUser', headerName: 'Updated By' },
  { field: 'editDate', headerName: 'Updated Date' },
  { field: 'loadingDate', headerName: 'Loading Date' },
  { field: 'placeOfLoading', headerName: 'Place of Loading' },
  { field: 'countryOfOrigin', headerName: 'Country of Origin' },
  { field: 'placeOfDelivery', headerName: 'Place of Delivery' },
  { field: 'countryOfDestination', headerName: 'Country of Destination' },
  { field: 'placeOfDischarge', headerName: 'Place of Discharge' },
  { field: 'customField1', headerName: 'PO Text UDF 1' },
  { field: 'customField2', headerName: 'PO Text UDF 2' },
  { field: 'customField4', headerName: 'PO Text UDF 3' },
  { field: 'customField5', headerName: 'PO Text UDF 4' },
];


const initialVisibleOLD = [
  'actions',
  'purchaseOrder',
  'buyerOwner',
  'type',
  'expectedReceiptDate',
  'supplier',
  'externalPoNumber',
  'totalOrderedQuantity',
  'openQuantity',
  'purchaseOrderDate',
  'asnNumbers',
];



const initialVisible = [
  'actions',
  'purchaseOrder',
  'buyerOwner',
  'supplier',
  'source',
  'status',
  'totalOrderedQuantity',
  'openQuantity',
  'asnNumbers',
  'closedDate',
  'totalWeight',
  'totalVolume',
  'expectedReceiptDate',
  'purchaseOrderDate',
];


function buildColumns(onView, onEdit) {
  const columns = [...allPossibleColumns];
  const actionsIndex = columns.findIndex((col) => col.field === 'actions');
  if (actionsIndex !== -1) {
    columns[actionsIndex] = actionMenuColumn(onView, onEdit);
  }
  return columns;
}

export default function PoDetailsTable({
  initialRows = [],
  onView,
  onEdit,
  onDataRefresh,
  onLinkOpen,
  showCreateButton = false,
  createButtonText = 'Create New PO',
  createButtonIcon = 'bi-plus-lg',
  onCreateClick,

  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,

}) {
  const [, setTableData] = React.useState(initialRows);
  const { hasPermission } = usePermission();

  const canExport = hasPermission("poDetails", "EXPORT");
  const canImport = hasPermission("poDetails", "IMPORT");

  React.useEffect(() => {
    setTableData(initialRows);
  }, [initialRows]);

  const columns = React.useMemo(() => buildColumns(onView, onEdit), [onView, onEdit]);


  const onManualRefresh = React.useCallback(
    async ({ filters = [], page: p = page ?? 0, pageSize: ps = pageSize ?? 10 } = {}) => {
      try {
        // 1) prefer parent provided onDataRefresh (keeps existing behavior)
        if (typeof onDataRefresh === "function") {
          await onDataRefresh({ filters, page: p, pageSize: ps });
          return;
        }

        // 2) fallback: call fetchPO directly and update local table state
        const res = await fetchPO(p, ps, []); // adjust filter shape if required by API
        const data = res?.data || [];
        setTableData(data);
      } catch (err) {
        console.error("Manual refresh failed:", err);
        toast.error(err?.message || "Refresh failed");
      }
    },
    [onDataRefresh, page, pageSize]
  );



  return (
    <CommonDataTable
      id="po-details-table"
      allPossibleColumns={columns}
      initialVisible={initialVisible}
      initialRows={initialRows}
      height={500}
      onDataRefresh={onDataRefresh}
      checkboxSelection
      exportImportPath="purchase-order"
      sampleFileUrl="/samples/purchase_order_sample.xlsx"
      sampleFileName="purchase-order"
      forInventory={true}
      showSyncWms
      enableImportButton={canImport}
      enableExportButton={canExport}
      showCreateButton={showCreateButton}
      createButtonText={createButtonText}
      createButtonIcon={createButtonIcon}
      onCreateClick={onCreateClick}
      linkColumns={{
        asnNumbers: {
          entity: 'ASN',
          parseValues: (val) =>
            Array.isArray(val)
              ? val
              : String(val ?? '')
                .split(/[,|]/)
                .map((s) => s.trim())
                .filter(Boolean),
        },
      }}
      onLinkOpen={onLinkOpen}
      page={page}
      pageSize={pageSize}
      rowCount={totalCount}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}

      // onManualRefresh={onManualRefresh}

    />
  );
}
